"use client";

import { motion, useReducedMotion } from "framer-motion";
import { Code2, Megaphone, Palette, ArrowDownRight, type LucideIcon } from "lucide-react";
import { SectionLabel } from "@/components/ui/SectionLabel";
import { HeroBackground, HeroFade } from "@/components/ui/HeroBackground";
import { useLang } from "@/lib/LanguageContext";
import { CATEGORY_ORDER, type ShowcaseCategory } from "./items";
import { ScrollHint } from "@/components/ui/ScrollHint"

const EASE = [0.22, 1, 0.36, 1] as const;

const CATEGORY_META: Record<string, { icon: LucideIcon; color: string }> = {
  marketing: { icon: Megaphone, color: "#FF419D" },
  creative: { icon: Palette, color: "#FFB76C" },
  web: { icon: Code2, color: "#38BDF8" },
};

export default function ServicesHero() {
  const { t } = useLang();
  const reduceMotion = useReducedMotion();
  const h = t.pageHeroes.services;
  const pickCategory = (id: string) =>
    window.dispatchEvent(new CustomEvent("services:category", { detail: id }));
  const categories = [...t.categoriesList].sort(
    (a, b) =>
      CATEGORY_ORDER.indexOf(a.id as ShowcaseCategory) - CATEGORY_ORDER.indexOf(b.id as ShowcaseCategory),
  );

  return (
    <section className="relative overflow-hidden pt-20 pb-28 sm:pt-36 sm:pb-16 flex items-center sm:items-start lg:items-center min-h-[100svh] lg:min-h-screen lg:pt-32 lg:pb-20">
      <HeroBackground src="workflow.webp" />
      {/* Drifting colour orbs over the image */}
      {[
        { c: "rgba(224,64,160,0.22)", cls: "left-[-10%] top-[10%] h-[520px] w-[520px]", x: [0, 60, 0], y: [0, 40, 0] },
        { c: "rgba(255,183,108,0.16)", cls: "right-[-8%] top-[-10%] h-[460px] w-[460px]", x: [0, -50, 0], y: [0, 50, 0] },
        { c: "rgba(56,189,248,0.12)", cls: "right-[20%] bottom-[-20%] h-[420px] w-[420px]", x: [0, 40, 0], y: [0, -30, 0] },
      ].map((o, i) => (
        <motion.div
          key={i}
          aria-hidden
          className={`pointer-events-none absolute rounded-full ${o.cls}`}
          style={{ background: `radial-gradient(circle, ${o.c}, transparent 65%)`, filter: "blur(70px)" }}
          animate={reduceMotion ? undefined : { x: o.x, y: o.y }}
          transition={{ duration: 18 + i * 4, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}

      <HeroFade />

      {/* Desktop: dark scrim behind the left-aligned copy so it reads over the image */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-[5] hidden sm:block"
        style={{
          background:
            "linear-gradient(90deg, rgba(10,10,15,0.92) 0%, rgba(10,10,15,0.8) 30%, rgba(10,10,15,0.45) 55%, rgba(10,10,15,0) 80%)",
        }}
      />

      <div className="container relative z-10 w-full">
        {/* Copy + category shortcuts */}
        <div className="relative isolate max-w-4xl max-sm:text-center">
          {/* Phones: dark, blurred pool behind the copy so it reads over the image */}
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-x-8 -inset-y-16 -z-10 sm:hidden"
            style={{
              background:
                "radial-gradient(ellipse 65% 55% at 50% 50%, rgba(10,10,15,0.85) 0%, rgba(10,10,15,0.6) 50%, rgba(10,10,15,0) 100%)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              maskImage: "radial-gradient(ellipse 60% 55% at 50% 50%, #000 45%, transparent 100%)",
              WebkitMaskImage: "radial-gradient(ellipse 60% 55% at 50% 50%, #000 45%, transparent 100%)",
            }}
          />
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <SectionLabel>{h.label}</SectionLabel>
          </motion.div>
          {/* Only one real <h1> (the mobile one, as Google indexes mobile);
              the longer desktop wording is a heading for screen readers only */}
          <motion.div
            className="mt-3 font-display font-extrabold text-[34px] sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.04] tracking-tight text-white"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.06, ease: EASE }}
          >
            <h1 className="sm:hidden">
              <span className="block">{h.titleMobile}</span>
              <span className="block text-gradient-warm">{h.titleGradientMobile}</span>
            </h1>
            <p role="heading" aria-level={1} className="hidden sm:block">
              {h.title.split("\n").map((line, i) => (
                <span key={i} className="block whitespace-nowrap">
                  {line}
                </span>
              ))}
              <span className="block whitespace-nowrap text-gradient-warm">{h.titleGradient}</span>
            </p>
          </motion.div>
          <motion.p
            className="hidden sm:block mt-5 max-w-xl text-lg text-white/75 leading-relaxed"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.14 }}
          >
            {h.description}
          </motion.p>

          {/* Mobile — the three directions as one row of icon tiles */}
          <div className="mt-7 grid grid-cols-3 gap-2.5 sm:hidden">
            {categories.map((cat, i) => {
              const meta = CATEGORY_META[cat.id];
              const Icon = meta.icon;
              return (
                <motion.button
                  key={cat.id}
                  type="button"
                  onClick={() => pickCategory(cat.id)}
                  className="group relative flex flex-col items-center gap-2.5 overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] px-2 pt-4 pb-3.5 backdrop-blur-md active:scale-[0.97] transition-transform"
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.25 + i * 0.08, ease: EASE }}
                >
                  <span
                    aria-hidden
                    className="absolute inset-x-4 top-0 h-px"
                    style={{ background: `linear-gradient(90deg, transparent, ${meta.color}, transparent)` }}
                  />
                  <span
                    className="flex h-11 w-11 items-center justify-center rounded-xl"
                    style={{
                      background: `${meta.color}1f`,
                      color: meta.color,
                      boxShadow: `inset 0 0 0 1px ${meta.color}33, 0 8px 24px -8px ${meta.color}66`,
                    }}
                  >
                    <Icon size={19} />
                  </span>
                  <span className="text-center text-[12px] font-medium leading-tight text-white/85">
                    {cat.short}
                  </span>
                </motion.button>
              );
            })}
          </div>

          <div className="mt-8 hidden sm:flex sm:flex-row sm:flex-wrap gap-3">
            {categories.map((cat, i) => {
              const meta = CATEGORY_META[cat.id];
              const Icon = meta.icon;
              return (
                <motion.button
                  key={cat.id}
                  type="button"
                  onClick={() => pickCategory(cat.id)}
                  className="group flex min-h-[52px] items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] py-2 pl-2 pr-4 text-left text-[15px] text-white/85 transition-all duration-300 hover:border-white/25 hover:bg-white/[0.06] cursor-pointer"
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.25 + i * 0.08, ease: EASE }}
                >
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
                    style={{ background: `${meta.color}1f`, color: meta.color }}
                  >
                    <Icon size={16} />
                  </span>
                  <span className="flex-1">{cat.label}</span>
                  <ArrowDownRight
                    size={16}
                    className="text-white/35 transition-all duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5 group-hover:text-white"
                  />
                </motion.button>
              );
            })}
          </div>
        </div>

      </div>
      <ScrollHint />
    </section>
  );
}
